/* sugar:assets/step-two.js@2.1.4 */
/*
 * Step 2 — the buy box's second step, opened in place.
 *
 * Markup contract (blocks/step-two.liquid):
 *   - `[data-step-two]` root inside a buy box column (`.product__info-container`), hidden until open
 *   - `[data-step-two-bar]` the title row: `[data-step-two-back]`, `[data-step-two-title]` whose
 *     data-template holds the title with [min]
 *   - `[data-step-two-media]` the optional desktop picture
 *   - data-mobile-media (hide | show), data-pin (true | false)
 *
 * While the buy box holds a Step 2, a click on one of its Product Buy Buttons outside the step (the
 * phone buy bar's included) opens the step instead of adding to cart. Opening sets the buy box's
 * other blocks and the page's other sections aside (chrome sections stay: announcement bars, Page
 * Styles, Page Background, Hide Header & Footer and the page's own header and footer, which mark
 * themselves with sw-chrome / sw-page-header / sw-page-footer comments), moves the title row to the
 * top of the section and the picture into the media column, scrolls to the top and adds a history
 * entry, so Back returns to step 1 where the shopper left it. A page address ending in #step-2
 * opens on step 2; ?bundle=N in it sets the count.
 *
 * Events out: `step-two:open` / `step-two:close` on document, detail { root, sectionId, count }.
 * A Bundle Builder inside the step reads the count from `step-two:open`.
 */
(function () {
  'use strict';
  if (window.SugarStepTwo) return;

  const ROOT = '[data-step-two]';
  const HASH = '#step-2';
  const CHROME = ['sw-chrome', 'sw-page-header', 'sw-page-footer'];
  const instances = new Map();
  let current = null;

  /* [one|many] after a number: the singular when the number just before it is 1 (Select at least 1 bag / 2 bags). */
  function plural(text) {
    return String(text || '').replace(/\[([^\[\]|<>]*)\|([^\[\]|<>]*)\]/g, (m, one, many, offset, whole) => {
      const nums = whole.slice(0, offset).match(/\d+(?:[.,]\d+)*/g);
      return nums && nums[nums.length - 1] === '1' ? one : many;
    });
  }

  function isChromeSection(section) {
    for (const node of section.childNodes) {
      if (node.nodeType === 8 && CHROME.indexOf(node.data.trim()) > -1) return true;
    }
    return false;
  }

  function hostOf(root) {
    return root.closest('product-info') || root.closest('.shopify-section');
  }

  /* How many units step 1 has picked: the selected offer, else the buy box's quantity input. */
  function countIn(section) {
    const offer = section && section.querySelector('[data-sugar-offers] .offer.is-selected');
    if (offer) {
      const requested = parseInt(offer.dataset.computedRequestedQuantity, 10);
      if (requested > 0) return requested;
      const quantity = parseInt(offer.dataset.quantity, 10);
      if (quantity > 0) return quantity;
    }
    const input = section && section.querySelector('input[name="quantity"]');
    const value = input ? parseInt(input.value, 10) : 0;
    return value > 0 ? value : 1;
  }

  function urlCount() {
    const n = parseInt(new URLSearchParams(window.location.search).get('bundle'), 10);
    return n > 0 ? n : 0;
  }

  class StepTwo {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || '';
      this.section = root.closest('.shopify-section');
      this.host = hostOf(root);
      this.column = root.closest('.product__info-container') || root.parentElement;
      this.slot = root;
      while (this.slot.parentElement && this.slot.parentElement !== this.column) this.slot = this.slot.parentElement;
      this.media = this.host ? this.host.querySelector('.product__media-wrapper') : null;
      this.grid = this.host ? this.host.querySelector('.sugar-layout-grid') : null;
      this.bar = root.querySelector('[data-step-two-bar]');
      this.picture = root.querySelector('[data-step-two-media]');
      this.titleEl = root.querySelector('[data-step-two-title]');
      this.titleTpl = this.titleEl ? this.titleEl.dataset.template || '' : '';
      this.isOpen = false;
      this.pushed = false;
      this.aside = [];
      this.onResize = () => { if (this.isOpen) this.measure(); };
      if (this.bar) this.bar.querySelector('[data-step-two-back]').addEventListener('click', () => this.back());
    }

    /* ---------------------------------------------------------- open / close */

    open(options) {
      const opts = options || {};
      const count = Math.max(1, opts.count || countIn(this.section));
      this.count = count;
      if (this.titleEl) this.titleEl.textContent = plural(this.titleTpl.split('[min]').join(String(count)));
      const detail = { root: this.root, sectionId: this.sectionId, count };
      if (this.isOpen) {
        document.dispatchEvent(new CustomEvent('step-two:open', { detail }));
        return;
      }
      if (current && current !== this) current.close({ silent: true });
      current = this;
      this.isOpen = true;
      this.returnY = window.scrollY;
      this.opener = opts.opener || null;

      this.setAside();
      if (this.bar && this.host) this.host.insertBefore(this.bar, this.host.firstChild);
      if (this.picture && this.media) this.media.insertBefore(this.picture, this.media.firstChild);
      this.root.classList.add('is-open');
      if (this.bar) this.bar.classList.add('is-open');
      if (this.picture) this.picture.classList.add('is-open');
      if (this.grid && this.picture) this.grid.parentElement.classList.add('sugar-step-two--wide');
      if (this.host) {
        this.host.classList.add('sugar-step-two--host');
        this.host.classList.toggle('sugar-step-two--host-picture', !!this.picture);
        this.host.classList.toggle('sugar-step-two--host-hide-m', this.root.dataset.mobileMedia !== 'show');
      }
      this.measure();
      window.addEventListener('resize', this.onResize, { passive: true });

      if (opts.push) {
        const url = new URL(window.location.href);
        url.searchParams.set('bundle', String(count));
        url.hash = HASH;
        history.pushState({ sugarStepTwo: this.sectionId }, '', url.pathname + url.search + url.hash);
        this.pushed = true;
      }
      if (opts.scroll !== false) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      const back = this.bar && this.bar.querySelector('[data-step-two-back]');
      if (back && opts.focus !== false) back.focus({ preventScroll: true });

      document.dispatchEvent(new CustomEvent('step-two:open', { detail }));
      try {
        if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
          Shopify.analytics.publish('sugar_step_two_opened', { count });
        }
      } catch (e) {
        /* analytics is optional */
      }
    }

    close(options) {
      const opts = options || {};
      if (!this.isOpen) return;
      this.isOpen = false;
      if (current === this) current = null;
      window.removeEventListener('resize', this.onResize);
      this.aside.forEach((el) => el.classList.remove('sugar-step-two--aside'));
      this.aside = [];
      this.root.classList.remove('is-open');
      if (this.bar) {
        this.bar.classList.remove('is-open');
        this.root.insertBefore(this.bar, this.root.firstChild);
      }
      if (this.picture) {
        this.picture.classList.remove('is-open');
        const body = this.root.querySelector('.sugar-step-two__body');
        this.root.insertBefore(this.picture, body);
      }
      if (this.grid) this.grid.parentElement.classList.remove('sugar-step-two--wide');
      if (this.host) {
        this.host.classList.remove('sugar-step-two--host', 'sugar-step-two--host-picture', 'sugar-step-two--host-hide-m', 'sugar-step-two--host-right', 'sugar-step-two--stacked');
      }
      const pinned = this.root.querySelector('.sugar-step-two__pinned');
      if (pinned) pinned.classList.remove('sugar-step-two__pinned');
      if (!opts.silent && opts.scroll !== false) {
        const y = this.returnY || 0;
        window.scrollTo({ top: y, left: 0, behavior: 'instant' });
        /* The browser may restore its own saved position after popstate; ours wins. */
        requestAnimationFrame(() => window.scrollTo({ top: y, left: 0, behavior: 'instant' }));
      }
      if (this.opener && opts.focus !== false && this.opener.isConnected) this.opener.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('step-two:close', { detail: { root: this.root, sectionId: this.sectionId } }));
    }

    /* The back arrow: undo our own history entry when we made one, else just close. */
    back() {
      if (this.pushed && history.state && history.state.sugarStepTwo === this.sectionId) {
        this.pushed = false;
        history.back();
        return;
      }
      this.pushed = false;
      if (window.location.hash === HASH) {
        const url = new URL(window.location.href);
        url.hash = '';
        history.replaceState(history.state, '', url.pathname + url.search);
      }
      this.close();
    }

    setAside() {
      const mark = (el) => {
        if (!el || el.classList.contains('sugar-step-two--aside')) return;
        el.classList.add('sugar-step-two--aside');
        this.aside.push(el);
      };
      if (this.column) {
        Array.prototype.forEach.call(this.column.children, (child) => {
          if (child === this.slot) return;
          if (/^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(child.tagName)) return;
          mark(child);
        });
      }
      const container = this.section && this.section.parentElement;
      if (container) {
        Array.prototype.forEach.call(container.children, (el) => {
          if (el === this.section || !el.classList.contains('shopify-section')) return;
          if (isChromeSection(el)) return;
          mark(el);
        });
      }
    }

    /*
     * Everything read first, then written: whether the columns stack, the side the media sits on,
     * the pinned bars above the title row, the row's own height and the gutters the pinned button
     * reaches across.
     */
    measure() {
      if (!this.host) return;
      /* Stacked = the info column spans the row (its width doesn't depend on whether the media shows) */
      const info = this.column && this.column.closest('.product__info-wrapper');
      const stacked = info && this.grid ? info.getBoundingClientRect().width > this.grid.getBoundingClientRect().width * 0.75 : window.innerWidth < 750;
      const right = !!(this.media && this.column && !stacked && this.media.getBoundingClientRect().left > this.column.getBoundingClientRect().left);
      let stick = 0;
      document.querySelectorAll('.shopify-section').forEach((el) => {
        if (el === this.section || el.classList.contains('sugar-step-two--aside')) return;
        if (!(el.compareDocumentPosition(this.section) & Node.DOCUMENT_POSITION_FOLLOWING)) return;
        const style = getComputedStyle(el);
        if (style.position === 'sticky' || style.position === 'fixed') stick += el.offsetHeight;
      });
      const barHeight = this.bar ? this.bar.offsetHeight : 0;
      const hostStyle = getComputedStyle(this.host);

      const part = this.pinnedPart();
      let gutterL = 0;
      let gutterR = 0;
      if (part) {
        /* The part's own edges, before the pinned margins pull it out to the screen's edges */
        const box = part.getBoundingClientRect();
        const style = getComputedStyle(part);
        gutterL = Math.max(0, Math.round(box.left - (parseFloat(style.marginLeft) || 0)));
        gutterR = Math.max(0, Math.round(document.documentElement.clientWidth - box.right - (parseFloat(style.marginRight) || 0)));
      }

      this.host.classList.toggle('sugar-step-two--stacked', stacked);
      this.host.classList.toggle('sugar-step-two--host-right', right);
      this.setVar(this.host, '--st-host-pl', hostStyle.paddingLeft);
      this.setVar(this.host, '--st-host-pr', hostStyle.paddingRight);
      this.setVar(this.host, '--st-stick', stick + 'px');
      this.setVar(this.host, '--st-stick-media', (stick + barHeight) + 'px');
      if (part) {
        this.setVar(part, '--st-gutter-l', gutterL + 'px');
        this.setVar(part, '--st-gutter-r', gutterR + 'px');
        if (!part.classList.contains('sugar-step-two__pinned')) part.classList.add('sugar-step-two__pinned');
      }
    }

    setVar(el, name, value) {
      if (el.style.getPropertyValue(name) !== value) el.style.setProperty(name, value);
    }

    /*
     * The part pinned on phones: the block holding the step's Buy Button that sits beside its
     * Bundle Builder (the button with the lines around it), else the button's own block.
     */
    pinnedPart() {
      if (this.root.dataset.pin !== 'true') return null;
      const body = this.root.querySelector('.sugar-step-two__body');
      const button = body && body.querySelector('.sugar-product-buy-button');
      if (!button) return null;
      const picker = body.querySelector('[data-bundle-builder]');
      let part = button;
      while (part.parentElement && part.parentElement !== body && !(picker && part.parentElement.contains(picker))) part = part.parentElement;
      return part;
    }
  }

  /* ---------------------------------------------------------------- wiring */

  function init(root) {
    if (!root || instances.has(root)) return instances.get(root);
    const step = new StepTwo(root);
    instances.set(root, step);
    return step;
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(ROOT).forEach((root) => init(root));
  }

  function liveInstances() {
    const list = [];
    instances.forEach((step, root) => {
      if (root.isConnected) list.push(step);
      else instances.delete(root);
    });
    return list;
  }

  /* The step a buy button outside every step opens: its own section's, else the only one on the page. */
  function stepFor(button) {
    if (button.closest(ROOT)) return null;
    const list = liveInstances();
    if (!list.length) return null;
    const section = button.closest('.shopify-section');
    const own = list.find((step) => step.section === section);
    if (own) return own;
    if (button.closest('sticky-custom, sticky-product')) return list.length === 1 ? list[0] : null;
    return null;
  }

  /* Capture on window runs before every form and cart script, so the click never becomes a submit. */
  window.addEventListener('click', (e) => {
    const button = e.target.closest && e.target.closest('.sugar-product-buy-button [type="submit"]');
    if (!button || button.disabled) return;
    const step = stepFor(button);
    if (!step) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    step.open({ push: !(window.Shopify && Shopify.designMode), opener: button });
  }, true);

  window.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form || !form.closest || !form.closest('.sugar-product-buy-button')) return;
    const step = stepFor(form);
    if (!step) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    step.open({ push: !(window.Shopify && Shopify.designMode) });
  }, true);

  window.addEventListener('popstate', () => {
    const list = liveInstances();
    if (!list.length) return;
    if (window.location.hash === HASH) {
      const target = list.find((step) => history.state && history.state.sugarStepTwo === step.sectionId) || list[0];
      target.pushed = true;
      target.open({ count: urlCount() || undefined, focus: false });
    } else if (current) {
      current.pushed = false;
      current.close();
    }
  });

  /* A link to #step-2 opens on step 2, once the buy box's other engines have booted. */
  function boot() {
    initAll();
    if (window.location.hash !== HASH) return;
    setTimeout(() => {
      const list = liveInstances();
      if (list.length && !current) list[0].open({ count: urlCount() || undefined, focus: false });
    }, 0);
  }

  /* ---------------------------------------------------------------- theme editor */

  /* A reloaded buy box comes back closed: give the page back what the old copy set aside. */
  document.addEventListener('shopify:section:load', (e) => {
    if (current && !current.root.isConnected) {
      current.aside.forEach((el) => el.classList.remove('sugar-step-two--aside'));
      current.aside = [];
      current = null;
    }
    initAll(e.target);
  });
  document.addEventListener('shopify:block:select', (e) => {
    const root = e.target && e.target.closest && e.target.closest(ROOT);
    if (root) {
      const step = init(root);
      step.open({ scroll: false, focus: false });
      if (e.target !== root && typeof e.target.scrollIntoView === 'function') e.target.scrollIntoView({ block: 'center' });
    } else if (current) {
      current.close({ scroll: false, focus: false });
    }
  });
  document.addEventListener('shopify:section:select', (e) => {
    if (current && e.target && !e.target.contains(current.root)) current.close({ scroll: false, focus: false });
  });

  window.SugarStepTwo = {
    init,
    initAll,
    open(root, options) { const step = init(root); if (step) step.open(options); },
    close(root) { const step = instances.get(root); if (step) step.close(); }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
